import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { apiClient, ApiError, logoutSession } from '@/lib/api';
import { useAuthStore, isAdminRole } from '@/lib/auth-store';
import type { MeView } from '@/lib/types';
import { getComplaintMetaApi } from '@/lib/complaints-api';
import {
  AuthTokenResponse,
  isSmsChallenge,
  SmsChallenge,
  VerifySmsResponse,
} from '@/lib/auth-contract';

interface LocationState {
  from?: string;
}

/**
 * Issue #21：管理后台两阶段登录（密码 → 短信验证）。
 *
 * ADMIN / SUPER_ADMIN 每次登录都必须短信验证，**不提供**「信任此设备」选项（后端也不会
 * 为管理员建立可信设备）。配置的非管理员投诉处理人沿用服务端返回的登录能力，不在前端
 * 伪造角色。密码阶段成功后按后端结果进入 OTP 或直接会话。
 * refresh token 全程走 HttpOnly Cookie（client='web'），响应体只含 accessToken（仅存内存）。
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);

  const [step, setStep] = useState<'password' | 'otp'>('password');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<SmsChallenge | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);

  const state = (location.state ?? {}) as LocationState;

  // 重发倒计时：每秒递减，到 0 可再次重发。
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  // 完成登录：同时拉取 /auth/me 与服务端能力元数据；非管理员只有 canHandle 才能进入。
  const completeWorkbenchLogin = useCallback(
    async (accessToken: string) => {
      setSession({ accessToken, user: null });
      try {
        const me = await apiClient.get<MeView>('/auth/me');
        const meta = await getComplaintMetaApi();
        const admin = isAdminRole(me.role);
        if (!admin && !meta.capabilities.canHandle) {
          // 撤销刚下发的 Cookie 会话（真实后端登出，logoutSession 内部一并清内存）。
          await logoutSession();
          setError('当前账号不是管理员或授权投诉处理人，无权访问该工作台。');
          return;
        }
        setSession({ accessToken, user: me });
        navigate(admin ? (state.from ?? '/overview') : '/complaints', {
          replace: true,
        });
      } catch (error) {
        // 能力确认失败时 fail-closed，不保留半登录会话。
        await logoutSession();
        throw error;
      }
    },
    [navigate, setSession, state.from],
  );

  async function handlePasswordSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // 200＝可信设备直发（管理员不会走到）；202＝需短信验证，返回 challenge 摘要。
      const result = await apiClient.post<AuthTokenResponse | SmsChallenge>(
        '/auth/login',
        { phone: phone.trim(), password, client: 'web' },
      );
      if (isSmsChallenge(result)) {
        setChallenge(result);
        setCode('');
        setResendIn(result.resendIntervalSec);
        setStep('otp');
        return;
      }
      if (!result?.accessToken) {
        throw new ApiError('登录响应缺少令牌', 502);
      }
      await completeWorkbenchLogin(result.accessToken);
    } catch (err: unknown) {
      setError(toLoginError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleOtpSubmit(event: FormEvent) {
    event.preventDefault();
    if (!challenge) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await apiClient.post<VerifySmsResponse>('/auth/verify-sms', {
        challengeId: challenge.challengeId,
        code: code.trim(),
        client: 'web',
      });
      if (!result?.accessToken) {
        throw new ApiError('验证响应缺少令牌', 502);
      }
      await completeWorkbenchLogin(result.accessToken);
    } catch (err: unknown) {
      setError(toLoginError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    if (!challenge || resendIn > 0 || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const refreshed = await apiClient.post<SmsChallenge>('/auth/resend-sms', {
        challengeId: challenge.challengeId,
      });
      setChallenge(refreshed);
      setResendIn(refreshed.resendIntervalSec);
    } catch (err: unknown) {
      setError(toLoginError(err));
    } finally {
      setSubmitting(false);
    }
  }

  function backToPassword() {
    setStep('password');
    setChallenge(null);
    setCode('');
    setError(null);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-brand-700 via-brand-600 to-brand-400 p-6">
      <div
        className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl"
        data-testid="login-card"
      >
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-500 text-white">
            金
          </div>
          <h1 className="text-xl font-semibold text-slate-900">
            金专商学院 · 管理后台
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            仅限管理员 / 超级管理员 / 授权投诉处理人登录
          </p>
        </div>

        {step === 'password' ? (
          <form className="space-y-4" onSubmit={handlePasswordSubmit} noValidate>
            <label className="block text-sm">
              <span className="mb-1 block text-slate-700">手机号</span>
              <input
                type="tel"
                autoComplete="username"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
                placeholder="请输入登录手机号"
                data-testid="login-phone"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-slate-700">密码</span>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
                placeholder="请输入密码"
                data-testid="login-password"
              />
            </label>
            {error ? (
              <div
                role="alert"
                data-testid="login-error"
                className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
              >
                {error}
              </div>
            ) : null}
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-brand-500 px-4 py-2 text-base font-medium text-white shadow transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="login-submit"
            >
              {submitting ? '登录中…' : '登录'}
            </button>
            <div className="pt-1 text-center text-xs text-slate-400">
              <Link
                to="/recovery"
                className="hover:text-brand-500 hover:underline"
                data-testid="login-recovery-link"
              >
                超级管理员应急登录
              </Link>
            </div>
          </form>
        ) : (
          // Issue #6.2：OTP 阶段以 challengeId 作为外层 key 强制重挂载，切换 challenge 时状态归零。
          <form
            key={challenge?.challengeId ?? 'otp'}
            className="space-y-4"
            onSubmit={handleOtpSubmit}
            noValidate
          >
            <p className="text-sm text-slate-600" data-testid="login-challenge-hint">
              验证码已发送至 <span className="font-medium">{challenge?.phoneMasked}</span>，
              请输入 6 位短信验证码完成登录。
            </p>
            <label className="block text-sm">
              <span className="mb-1 block text-slate-700">短信验证码</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-center text-lg tracking-[0.5em] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
                placeholder="______"
                data-testid="login-otp"
                autoFocus
              />
            </label>
            {error ? (
              <div
                role="alert"
                data-testid="login-error"
                className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
              >
                {error}
              </div>
            ) : null}
            <button
              type="submit"
              disabled={submitting || code.length < 6}
              className="w-full rounded-lg bg-brand-500 px-4 py-2 text-base font-medium text-white shadow transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="login-otp-submit"
            >
              {submitting ? '验证中…' : '验证并登录'}
            </button>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <button
                type="button"
                onClick={backToPassword}
                className="hover:text-brand-500 hover:underline"
                data-testid="login-otp-back"
              >
                返回上一步
              </button>
              <button
                type="button"
                onClick={handleResend}
                disabled={resendIn > 0 || submitting}
                className="hover:text-brand-500 hover:underline disabled:cursor-not-allowed disabled:text-slate-300 disabled:no-underline"
                data-testid="login-resend"
              >
                {resendIn > 0 ? `重新发送（${resendIn}s）` : '重新发送验证码'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/** 统一登录错误文案：401 一律「手机号或密码错误」（不泄露账号存在性），其余取后端 message。 */
function toLoginError(err: unknown): string {
  if (err instanceof ApiError) {
    return err.status === 401 ? '手机号或密码错误' : err.message;
  }
  if (err instanceof Error) return err.message;
  return '登录失败，请稍后再试';
}
